import { findPhoneNumberAreaCode } from './utils'

export const formatPhonenumberByCountryConvention = (country: string, number: string) => {
  if (!number || !country) {
    return number
  }
  const areaCode = findPhoneNumberAreaCode(country, number.replace(/[^\d+]/g, '') || '')
  if (!areaCode) {
    // if we don't match an areacode, we return value as is
    return number
  }
  const cleanedNumberWithPrefix = number.replace(/[^\d+]/g, '')
  // massive edge case when people are adding their own + character we simply return the number unformatted
  if (
    cleanedNumberWithPrefix.substring(0, areaCode.dialCode.length) !== areaCode.dialCode &&
    cleanedNumberWithPrefix.substring(0, 1) === '+'
  ) {
    return number
  }
  const numberWithoutPrefixArea =
    cleanedNumberWithPrefix.substring(0, areaCode.dialCode.length) !== areaCode.dialCode
      ? cleanedNumberWithPrefix
      : cleanedNumberWithPrefix.substring(areaCode.dialCode.length)
  const numberLeadZero = numberWithoutPrefixArea.substring(0, 1) === '0' ? numberWithoutPrefixArea : `0${numberWithoutPrefixArea}`
  const numberLeadZeroLength = numberLeadZero.length
  switch (areaCode.dialCode) {
    case `+31`: // NL international code
      // mobile numbers always start with a 06 and a 10 digit length
      if (numberLeadZeroLength === 10 && numberLeadZero.substring(0, 2) === '06') {
        return `${areaCode.dialCode} ${numberLeadZero.substring(1, 2)} ${numberLeadZero.substring(2)}`
      } else if (numberLeadZeroLength === 10 && numberLeadZero.substring(0, 2) !== '06') {
        // landline numbers are 3 or 4 digits including lead 0
        // if a number doesnt start with 06 it should be in the list of possible landlines
        if (dutchLandlinesPrefixes.includes(numberLeadZero.substring(0, 3))) {
          return `${areaCode.dialCode} ${numberLeadZero.substring(1, 3)} ${numberLeadZero.substring(3)}`
        } else if (dutchLandlinesPrefixes.includes(numberLeadZero.substring(0, 4))) {
          return `${areaCode.dialCode} ${numberLeadZero.substring(1, 4)} ${numberLeadZero.substring(4)}`
        }
      }
      return number
    case `+32`: // BE international code
      // mobile phone numbers 10 digits > start with 04XX for provider and subgroup then subscriber number
      if (numberLeadZeroLength === 10) {
        return `${areaCode.dialCode} ${numberLeadZero.substring(1, 4)} ${numberLeadZero.substring(4, 6)} ${numberLeadZero.substring(
          6,
          8
        )} ${numberLeadZero.substring(8)}`
      }
      // landlines are 9 digits > however the provider code can be either 2 or 3 digits long
      // we we have a list of prefixes to check against
      if (numberLeadZeroLength === 9) {
        if (belgiumLandlinesPrefixThree.includes(numberLeadZero.substring(0, 3))) {
          return `${areaCode.dialCode} ${numberLeadZero.substring(1, 3)} ${numberLeadZero.substring(3, 5)} ${numberLeadZero.substring(
            5,
            7
          )} ${numberLeadZero.substring(7)}`
        } else if (belgiumLandlinesPrefixTwo.includes(numberLeadZero.substring(0, 2))) {
          return `${areaCode.dialCode} ${numberLeadZero.substring(1, 2)} ${numberLeadZero.substring(2, 5)} ${numberLeadZero.substring(
            5,
            7
          )} ${numberLeadZero.substring(7)}`
        }
      }
      return number
    case `+33`: // FR international code
      // trim off special characters to properly test total length of string
      if (numberLeadZeroLength === 10) {
        return `${areaCode.dialCode} ${numberLeadZero.substring(1, 2)} ${numberLeadZero.substring(2, 4)} ${numberLeadZero.substring(
          4,
          6
        )} ${numberLeadZero.substring(6, 8)} ${numberLeadZero.substring(8)}`
      }
      return number
    default:
      return number
  }
}

const belgiumLandlinesPrefixThree = [
  '010',
  '011',
  '012',
  '013',
  '014',
  '015',
  '016',
  '019',
  '050',
  '051',
  '052',
  '053',
  '054',
  '055',
  '056',
  '057',
  '058',
  '059',
  '060',
  '061',
  '063',
  '064',
  '065',
  '067',
  '068',
  '069',
  '071',
  '080',
  '081',
  '082',
  '083',
  '084',
  '085',
  '086',
  '087',
  '089'
]

const belgiumLandlinesPrefixTwo = ['02', '03', '04', '09']

const dutchLandlinesPrefixes = [
  '00',
  '010',
  '0111',
  '0113',
  '0114',
  '0115',
  '0117',
  '0118',
  '013',
  '014',
  '015',
  '0161',
  '0162',
  '0164',
  '0165',
  '0166',
  '0167',
  '0168',
  '0172',
  '0174',
  '0180',
  '0181',
  '0182',
  '0183',
  '0184',
  '0186',
  '0187',
  '020',
  '0222',
  '0223',
  '0224',
  '0226',
  '0227',
  '0228',
  '0229',
  '023',
  '024',
  '0251',
  '0252',
  '0255',
  '026',
  '0294',
  '0297',
  '0299',
  '030',
  '0313',
  '0314',
  '0315',
  '0316',
  '0317',
  '0318',
  '0320',
  '0321',
  '033',
  '0341',
  '0342',
  '0343',
  '0344',
  '0345',
  '0346',
  '0347',
  '0348',
  '035',
  '036',
  '038',
  '040',
  '0411',
  '0412',
  '0413',
  '0416',
  '0418',
  '043',
  '044',
  '045',
  '046',
  '0475',
  '0478',
  '0481',
  '0485',
  '0486',
  '0487',
  '0488',
  '0492',
  '0493',
  '0495',
  '0497',
  '0499',
  '050',
  '0511',
  '0512',
  '0513',
  '0514',
  '0515',
  '0516',
  '0517',
  '0518',
  '0519',
  '0521',
  '0522',
  '0523',
  '0524',
  '0525',
  '0527',
  '0528',
  '0529',
  '053',
  '0541',
  '0543',
  '0544',
  '0545',
  '0546',
  '0547',
  '0548',
  '055',
  '0561',
  '0562',
  '0566',
  '0570',
  '0571',
  '0572',
  '0573',
  '0575',
  '0577',
  '0578',
  '058',
  '0591',
  '0592',
  '0593',
  '0594',
  '0595',
  '0596',
  '0597',
  '0598',
  '0599',
  '070',
  '071',
  '072',
  '073',
  '074',
  '075',
  '076',
  '077',
  '078',
  '079'
]
